import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Paper,
    Typography,
    Grid,
    Divider,
    TextField,
    ButtonGroup,
    Button,
    FormControl,
    InputLabel,
    Select,
    Chip,
    MenuItem,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import { ColorPicker } from 'mui-color';

import {
    toggleZoneDrafting, removeZoneDraftCoords, addZoneDraftCoords,
    updateZoneDraftColor, clearZoneDraftCoords,
    zoneDraftCoordsSelector,
    saveZone, zoneEditDataSelector,
    setZoneDetailView, removeZoneDetailView, editZone,
} from '../../features/places/placesSlice';
import { setNeedBounding } from '../../features/map/mapSlice';
import { tagList } from '../../utils/placeTagsOptions';

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: useTheme().spacing(1),
        width: 200,
    },
    label: {
        margin: useTheme().spacing(1),
    },
    selectEmpty: {
        marginTop: useTheme().spacing(2),
    },
    margin: {
        margin: useTheme().spacing(1),
    },
    textField: {
        width: '25ch',
    },
    chip: {
        margin: useTheme().spacing(0.5),
    },
}));



const ZoneCreateForm = ({ setListViewCallback }) => {

    const classes = useStyles();
    const dispatch = useDispatch();
    const zoneDraftCoords = useSelector(zoneDraftCoordsSelector);
    const zoneEditData = useSelector(zoneEditDataSelector);

    const [zoneId, setZoneId] = useState(null);
    const [zoneName, setZoneName] = useState("");
    const [zoneColor, setZoneColor] = useState("#CF3827");
    const [zoneTags, setZoneTags] = useState([]);

    const [formMode, setFormMode] = useState("create");
    //const [validationError, setValidationError] = useState("");


    useEffect(() => {
        dispatch(toggleZoneDrafting(true));
        return () => {
            dispatch(toggleZoneDrafting(false));
        }
    }, [dispatch]);



    const loadEditData = useCallback(
        () => {
            console.log("In Load edit", zoneEditData);
            setZoneId(zoneEditData.zone_id);
            setZoneName(zoneEditData.zone_name);
            setZoneColor(zoneEditData.zone_color);
            setZoneTags(zoneEditData.zone_tags ?? []);
            dispatch(clearZoneDraftCoords());
            zoneEditData.zone_coords.forEach(c => dispatch(addZoneDraftCoords(c)));
        },
        [dispatch, zoneEditData],
    )

    useEffect(() => {
        if (zoneEditData.zone_id) {
            setFormMode("edit");
            dispatch(setZoneDetailView(zoneEditData.zone_id));
            dispatch(setNeedBounding(true));
            loadEditData();
        } else {
            setFormMode("create");
            dispatch(removeZoneDetailView());
            setZoneName("");
            setZoneColor("#CF3827");
            setZoneTags([]);
            dispatch(clearZoneDraftCoords());
        }
    }, [zoneEditData, loadEditData, dispatch]);




    const onTagChange = (e) => {
        let tags = [...zoneTags];
        if (tags.indexOf(e.target.value) === -1) {
            //console.log("tags before push", tags, e.target.value);
            tags.push(e.target.value);
        }
        setZoneTags(tags);
    }

    const tagDeselect = (chip) => {
        const index = zoneTags.indexOf(chip);
        if (index !== -1) {
            let tags = [...zoneTags];
            tags.splice(index, 1);
            setZoneTags(tags);
        }
    }

    const colorChange = (value) => {
        //console.log("color onChange=", value);
        setZoneColor(`#${value.hex}`);
        dispatch(updateZoneDraftColor(`#${value.hex}`));
    }

    const removeVertex = (e) => {
        //console.log("e", e);
        dispatch(removeZoneDraftCoords(e));
    }

    const resetForm = () => {
        if (formMode === "create") {
            dispatch(clearZoneDraftCoords());
            setZoneColor(`#CF3827`);
            dispatch(updateZoneDraftColor(`#CF3827`));
            setZoneName("");
            setZoneTags([]);
        } else {
            loadEditData();
        }
    }

    const cancelEdit = () => {
        dispatch(editZone({}));
        dispatch(removeZoneDetailView());
        dispatch(clearZoneDraftCoords());
        setZoneColor(`#CF3827`);
        dispatch(updateZoneDraftColor(`#CF3827`));
        setZoneName("");
        setZoneTags([]);
        setListViewCallback('zoneList')
    }

    const submit = () => {
        dispatch(saveZone({
            zone_name: zoneName,
            zone_color: zoneColor,
            zone_coords: zoneDraftCoords,
            zone_tags: zoneTags,
            zone_id: zoneId
        }))
        setListViewCallback('zoneList');
    }


    const validator = () => {
        let validationFail = false;
        //let message = "";
        const nameRegex = new RegExp(/^([a-zA-Z0-9 _-]+)$/i);
        if (zoneDraftCoords.length < 2) {
            validationFail = true;
            //message = "Minimum 3 points needed for making a zone\n";
        }
        if (zoneName.length > 30 || zoneName.length < 4) {
            validationFail = true;
            //message += "Zone Name must be at least 4 charaters and a max 30 characters long\n";
        }
        if (!nameRegex.test(zoneName)) {
            validationFail = true;
            //message += "Zone name can contain only letters, numbers and space. Special characters are not allowed.\n";
        }
        //setValidationError(message);
        //console.log("Valid", validationFail, message);
        return validationFail;

    }

    return (
        <Paper elevation={3}>
            <div style={{ margin: '10px' }}>
                <Grid container spacing={1}>
                    <Grid item sm={12}><Typography variant="h6">Create Zone</Typography></Grid>
                    <Grid item sm={12}><Divider /></Grid>
                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <TextField
                                variant="standard"
                                id="zoneName"
                                label="Name of the Zone"
                                value={zoneName}
                                onChange={e => setZoneName(e.target.value)}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <InputLabel id="reportTypeLabel">Zone Tags</InputLabel>
                            <Select
                                variant="standard"
                                labelId="reportTypeLabel"
                                id="reportTypeSelect"
                                onChange={onTagChange}
                                value={""}
                            >
                                {
                                    tagList.map(ev => <MenuItem key={ev} value={ev}>{ev}</MenuItem>)
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>
                        {zoneTags.map((v, i) =>
                            <Chip
                                size="small"
                                label={v}
                                onDelete={() => tagDeselect(v)}
                                className={classes.chip}
                                key={i}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Grid>

                    <Grid item container sm={12}>
                        <FormControl className={classes.formControl}>
                            <Typography variant="body1"><span style={{ color: zoneColor }}>Zone Color</span></Typography>
                            <ColorPicker defaultValue="transparent" value={zoneColor} onChange={colorChange} hideTextfield />
                        </FormControl>
                    </Grid>

                    <Grid item sm={12}>
                        <FormControl className={classes.formControl}>
                            <Typography variant="body1">Zone Vertices: {zoneDraftCoords.length}</Typography>
                        </FormControl>
                    </Grid>
                    <Grid item sm={12}>


                        {zoneDraftCoords.map((v, i) =>
                            <Chip
                                size="small"
                                label={`Vertex ${i + 1}`}
                                onDelete={() => removeVertex(i)}
                                className={classes.chip}
                                key={i}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Grid>

                    <Grid item sm={12}>
                        <ButtonGroup variant="contained">
                            <Button onClick={cancelEdit} color="secondary">Cancel</Button>
                            <Button onClick={resetForm}>Reset</Button>
                            <Button onClick={submit} disabled={validator()} color="primary">Submit</Button>
                        </ButtonGroup>
                    </Grid>
                    <Grid item sm={12}></Grid>
                </Grid>
            </div>

        </Paper>
    );
}

export default ZoneCreateForm;